// Input — text/email/password/number with label, helper, and error slots.
//
// Layout:
//   [label]      ← above the field, text-sm font-medium
//   [<input>]    ← border-default at rest, Teal on focus, Ember on error
//   [helper or error]  ← below; helper text-tertiary, error in Ember
//
// Focus ring: shadow-focus token (Teal at 15% opacity).
// Error state: border becomes Ember; helper area shows error message.

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";

import { cn } from "./cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, helper, error, type = "text", className, id, ...rest },
  ref,
) {
  // Auto-generate an id so the label can `htmlFor` it. Callers can still
  // override by passing an explicit id.
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedById = error || helper ? `${inputId}-desc` : undefined;
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1">
      {label !== undefined && (
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-text-primary"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        type={type}
        aria-invalid={hasError || undefined}
        aria-describedby={describedById}
        data-error={hasError ? "true" : "false"}
        className={cn(
          "rounded-md border bg-raised px-3 py-2 text-base text-text-primary outline-none",
          "placeholder:text-text-tertiary transition-fast ease-out",
          "focus-visible:shadow-focus focus-visible:border-teal",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError ? "border-ember" : "border-border-default",
          className,
        )}
        {...rest}
      />
      {hasError ? (
        <p id={describedById} className="text-sm text-ember-ink" role="alert">
          {error}
        </p>
      ) : helper !== undefined ? (
        // text-secondary, not tertiary: helper text is content, and tertiary
        // (#8A8880) on the canvas (#FAFAF7) is 3.40:1 — below the 4.5:1 WCAG
        // AA floor for 13px text. Caught by the onboarding 390px axe check on
        // the first helper to land on a surface that runs one (PR #108);
        // secondary is 6.40:1. Placeholders may stay tertiary; this is not a
        // placeholder.
        <p id={describedById} className="text-sm text-text-secondary">
          {helper}
        </p>
      ) : null}
    </div>
  );
});
